
import React, { useState, useEffect, useRef } from 'react';
import { AppMode, Language, ServiceDefinition } from '../types';
import { TRANSLATIONS } from '../constants';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  services: ServiceDefinition[];
  onNavigate: (mode: AppMode) => void;
  onExecuteAction: (action: string, params?: any) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ 
  isOpen, onClose, language, services, onNavigate, onExecuteAction 
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const isRtl = language === 'ar';
  const t = TRANSLATIONS[language];

  // Logic to detect if current query is a command
  const getCommandInfo = (q: string) => {
    const parts = q.trim().toLowerCase().split(' ');
    const base = parts[0];
    const action = parts[1];
    const target = parts.slice(2).join(' ');

    if (base === 'flow') {
      if (action === 'create') return { type: 'command', icon: 'fa-plus-circle', label: isRtl ? `إنشاء تدفق باسم: ${target || '...'}` : `Create flow: ${target || '...'}`, cmd: 'flow-create', params: { name: target } };
      if (action === 'delete') return { type: 'command', icon: 'fa-trash-can', label: isRtl ? `حذف تدفق: ${target || '...'}` : `Delete flow: ${target || '...'}`, cmd: 'flow-delete', params: { id: target }, danger: true };
    }
    if (base === 'plan') {
      if (action === 'upgrade') return { type: 'command', icon: 'fa-crown', label: isRtl ? `ترقية إلى باقة: ${target || '...'}` : `Upgrade to: ${target || '...'}`, cmd: 'plan-upgrade', params: { type: target } };
    }
    if (base === 'service') {
      if (action === 'enable' || action === 'disable') return { type: 'command', icon: 'fa-toggle-on', label: isRtl ? `${action === 'enable' ? 'تفعيل' : 'تعطيل'} خدمة: ${target || '...'}` : `${action} service: ${target || '...'}`, cmd: `service-${action}`, params: { id: target } };
    }
    if (base === 'set') {
      if (action === 'theme') return { type: 'command', icon: 'fa-moon', label: isRtl ? `تغيير المظهر إلى: ${target || '...'}` : `Set theme: ${target || '...'}`, cmd: 'set-theme', params: { val: target } };
    }
    return null;
  };

  const currentCmd = getCommandInfo(query);

  const navigationItems = [
    { id: 'chat', label: t.chatRoom, icon: 'fa-message', mode: 'chat' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'live', label: t.liveMode, icon: 'fa-microphone-lines', mode: 'live' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'flows', label: t.flowsMode, icon: 'fa-diagram-project', mode: 'flows' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'services', label: t.servicesMode, icon: 'fa-cubes', mode: 'services' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'robot', label: t.robotMode, icon: 'fa-robot', mode: 'robot' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'integrations', label: t.integrationsMode, icon: 'fa-network-wired', mode: 'integrations' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
    { id: 'docs', label: t.docsMode, icon: 'fa-book', mode: 'docs' as AppMode, category: isRtl ? 'انتقال' : 'Navigation', type: 'nav' },
  ];

  const searchItems = services.map(s => ({
    id: `svc-${s.id}`,
    label: s.title,
    icon: s.icon,
    type: 'service',
    category: isRtl ? 'خدمة' : 'Service',
    params: s
  }));

  const allFiltered = [
    ...(currentCmd ? [{ ...currentCmd, id: 'cmd-dynamic', category: isRtl ? 'أمر تنفيذي' : 'Terminal Command' }] : []),
    ...navigationItems.filter(i => i.label.toLowerCase().includes(query.toLowerCase())),
    ...searchItems.filter(i => i.label.toLowerCase().includes(query.toLowerCase()))
  ].map(item => ({ ...item, danger: (item as any).danger || false }));

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % allFiltered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + allFiltered.length) % allFiltered.length);
      } else if (e.key === 'Enter') {
        handleSelect(allFiltered[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allFiltered, selectedIndex]);

  const handleSelect = (item: any) => {
    if (!item) return;
    // Disambiguate item types using a common 'type' property
    if (item.type === 'command') onExecuteAction(item.cmd, item.params);
    else if (item.type === 'nav') onNavigate(item.mode);
    else if (item.type === 'service') onExecuteAction('run-service', item.params);
    
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-start justify-center pt-[15vh] px-4">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-xl animate-in fade-in duration-300" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-white dark:bg-[#0f0f13] rounded-[28px] shadow-[0_0_80px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-white/10 overflow-hidden animate-in zoom-in-95 slide-in-from-top-8 duration-300 flex flex-col max-h-[60vh]">
        
        {/* Search Header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
          <i className={`fas ${currentCmd ? 'fa-terminal text-emerald-500 animate-pulse' : 'fa-search text-slate-400'} text-lg`}></i>
          <input 
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            placeholder={isRtl ? "اكتب أمراً (مثل: flow create) أو ابحث..." : "Type command (e.g., flow create) or search..."}
            className="flex-1 bg-transparent border-none focus:ring-0 text-lg font-bold text-slate-800 dark:text-white placeholder:text-slate-400 outline-none"
          />
          {query && <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-600"><i className="fas fa-times-circle"></i></button>}
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
          {allFiltered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-slate-400 font-bold">{isRtl ? 'لا توجد نتائج' : 'No results'}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {allFiltered.map((item, index) => {
                const isSelected = index === selectedIndex;
                const isFirstOfCategory = index === 0 || allFiltered[index - 1].category !== item.category;

                return (
                  <React.Fragment key={item.id}>
                    {isFirstOfCategory && (
                      <div className="px-4 py-2 mt-2">
                        <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">{item.category}</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`
                        w-full flex items-center gap-4 px-4 py-3 rounded-2xl transition-all text-start
                        ${isSelected ? 'bg-emerald-500 text-white shadow-lg' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'}
                      `}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm ${isSelected ? 'bg-white/20' : 'bg-slate-100 dark:bg-white/10'}`}>
                        <i className={`fas ${item.icon}`}></i>
                      </div>
                      <div className="flex-1">
                         <p className="font-bold text-sm">{item.label}</p>
                         {item.type === 'command' && <p className={`text-[9px] ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>Executable Terminal Action</p>}
                      </div>
                      {isSelected && <i className="fas fa-level-down-alt rotate-90 opacity-50"></i>}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Hints */}
        <div className="px-6 py-3 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-black/40 flex justify-between items-center text-[9px] font-black text-slate-400 uppercase tracking-widest">
           <div className="flex gap-4">
              <span><i className="fas fa-keyboard mr-1"></i> Use keywords: flow, plan, set, service</span>
           </div>
           <button onClick={() => { onNavigate('docs'); onClose(); }} className="hover:text-emerald-500 transition-colors">Command Guide <i className="fas fa-book ml-1"></i></button>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
